/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useEffect, useState } from 'react';
import axios from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { Alert } from '../shared/Alert';
import { FormModal } from '../shared/FormModal';
import './PasswordResetRequestDialog.css';

export interface PasswordResetRequestDialogProps {
  isOpen: boolean;
  initialEmail?: string;
  onClose: () => void;
}

export const PasswordResetRequestDialog: React.FC<PasswordResetRequestDialogProps> = ({
  isOpen,
  initialEmail = '',
  onClose,
}) => {
  const { language } = useAuth();
  const t = useTranslation(language);
  const fallback =
    language === 'tr'
      ? {
          selfServiceTitle: 'Şifremi Unuttum',
          selfServiceDescription:
            'Kurumsal hesabınızın e-posta adresini girin. Kayıt eşleşirse 30 dakika geçerli, tek kullanımlık bir bağlantı gönderilir.',
          selfServiceEmailLabel: 'E-posta',
          selfServiceSubmit: 'Bağlantı iste',
          selfServiceSubmitting: 'İstek gönderiliyor...',
          selfServiceAcknowledgement:
            'Bu e-posta adresi kayıtlıysa şifre sıfırlama bağlantısı gönderilecektir. Lütfen gelen kutunuzu ve istenmeyen postaları kontrol edin.',
          selfServiceClose: 'Kapat',
          selfServiceCancel: 'İptal',
          selfServiceError: 'Şifre sıfırlama isteği şu anda tamamlanamadı. Lütfen daha sonra tekrar deneyin.',
          selfServiceRateLimited: 'Çok fazla istek gönderdiniz. Lütfen daha sonra tekrar deneyin.',
        }
      : {
          selfServiceTitle: 'Forgot Password',
          selfServiceDescription:
            'Enter your corporate account email. If it matches an account, a one-time link valid for 30 minutes will be sent.',
          selfServiceEmailLabel: 'Email',
          selfServiceSubmit: 'Request link',
          selfServiceSubmitting: 'Sending request...',
          selfServiceAcknowledgement:
            'If this email is registered, a password reset link will be sent. Please check your inbox and spam folder.',
          selfServiceClose: 'Close',
          selfServiceCancel: 'Cancel',
          selfServiceError: 'The password reset request could not be completed. Please try again later.',
          selfServiceRateLimited: 'Too many requests. Please try again later.',
        };
  const copy = { ...fallback, ...(t.passwordReset ?? {}) };
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEmail(initialEmail.trim());
    setSubmitting(false);
    setError('');
    setSuccess(false);
  }, [initialEmail, isOpen]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting || success) return;
    setError('');
    try {
      setSubmitting(true);
      await axios.post('/api/auth/password-reset/request', {
        email: email.trim(),
      });
      setSuccess(true);
    } catch (requestError: any) {
      setError(requestError?.response?.status === 429 ? copy.selfServiceRateLimited : copy.selfServiceError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      title={copy.selfServiceTitle}
      onClose={onClose}
      closeAriaLabel={copy.selfServiceClose}
      closeOnOverlayClick={false}
      surfaceScope="viewport"
      maximizable={false}
      size="sm"
      contentClassName="password-reset-request-dialog"
      footer={
        success ? (
          <button type="button" className="btn-primary" onClick={onClose}>
            {copy.selfServiceClose}
          </button>
        ) : (
          <>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              {copy.selfServiceCancel}
            </button>
            <button
              type="submit"
              className="btn-primary"
              form="password-reset-request-form"
              disabled={submitting || !email.trim()}
            >
              {submitting ? copy.selfServiceSubmitting : copy.selfServiceSubmit}
            </button>
          </>
        )
      }
    >
      <div className="password-reset-request-body">
        <p className="password-reset-request-description">{copy.selfServiceDescription}</p>
        <Alert type="error" message={error} onDismiss={() => setError('')} />
        {success ? (
          <Alert type="success" message={copy.selfServiceAcknowledgement} />
        ) : (
          <form id="password-reset-request-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password-reset-request-email">{copy.selfServiceEmailLabel} *</label>
              <input
                id="password-reset-request-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                maxLength={254}
                required
                autoFocus
                disabled={submitting}
              />
            </div>
          </form>
        )}
      </div>
    </FormModal>
  );
};

PasswordResetRequestDialog.displayName = 'PasswordResetRequestDialog';
